/* eslint-disable @typescript-eslint/no-unused-vars */
import { Box, Button, Divider, Icon, Paper, Skeleton, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';

const RotuloBotao: React.FC<{ label: string; hotkey?: string }> = ({ label, hotkey }) => (
  <Box display="flex" alignItems="baseline" gap={0.6}>
    <Typography variant='button' fontWeight={600} whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
      {label}
    </Typography>
    {hotkey && (
      <Typography variant='caption' color='text.secondary' fontWeight={600} whiteSpace="nowrap">
        {hotkey}
      </Typography>
    )}
  </Box>
);

interface IFerramentasDeDetalheProps {
  textoBotaoNovo?: string;
  mostrarBotaoNovo?: boolean;
  mostrarBotaoVoltar?: boolean;
  mostrarBotaoApagar?: boolean;
  mostrarBotaoSalvar?: boolean;
  mostrarBotaoSalvarEFechar?: boolean;
  mostrarBotaoImpressao?: boolean;
  // Carnê de pagamento (1 card por parcela da venda) — separado da
  // impressão normal (nota/comprovante), só faz sentido numa venda já
  // faturada (à vista ou a prazo), ver DetalheDeVenda.tsx.
  mostrarBotaoCarne?: boolean;
  mostrarBotaoCarneCarregando?: boolean;
  mostrarBotaoAdicionarItem?: boolean;
  mostrarBotaoAdicionarItemCarregando?: boolean;
  mostrarBotaoDevolucao?: boolean;
  mostrarBotaoDevolucaoCarregando?: boolean;
  mostrarBotaoCancelarVendaCarregando?: boolean;
  mostrarBotaoCancelarVenda?: boolean;

  disableBotaoDevolucao?: boolean;
  disableBotaoSalvar?: boolean;
  disableBotaoSalvarEFechar?: boolean;
  disableBotaoApagar?: boolean;
  disableBotaoCancelarVenda?: boolean;
  disableBotaoAdicionarItem?: boolean;

  mostrarBotaoNovoCarregando?: boolean;
  mostrarBotaoVoltarCarregando?: boolean;
  mostrarBotaoApagarCarregando?: boolean;
  mostrarBotaoSalvarCarregando?: boolean;
  mostrarBotaoSalvarEFecharCarregando?: boolean;
  mostrarBotaoImpressaoCarregando?: boolean;

  aoClicarEmNovo?: () => void;
  aoClicarEmVoltar?: () => void;
  aoClicarEmApagar?: () => void;
  aoClicarEmSalvar?: () => void;
  aoClicarEmSalvarEFechar?: () => void;
  aoClicarEmImpressao?: () => void;
  aoClicarEmCarne?: () => void;
  aoClicarEmDevolucao?: () => void;
  aoClicarEmCancelarVenda?: () => void;
  aoClicarEmAdicionarItem?: () => void;
  // Conteúdo extra no fim da barra, alinhado à direita (ex.: seletor de modo
  // de busca em DetalheDeVenda.tsx).
  conteudoFinal?: React.ReactNode;
}
export const FerramentasDeDetalheVenda: React.FC<IFerramentasDeDetalheProps> = ({
  textoBotaoNovo = 'Novo',
  mostrarBotaoNovo = true,
  mostrarBotaoVoltar = true,
  mostrarBotaoApagar = true,
  mostrarBotaoSalvar = true,
  mostrarBotaoImpressao = true,
  mostrarBotaoCarne = false,
  mostrarBotaoCarneCarregando = false,
  mostrarBotaoCancelarVenda = false,
  mostrarBotaoAdicionarItem = false,
  disableBotaoSalvar = false,
  disableBotaoDevolucao = true,
  disableBotaoSalvarEFechar = false,
  disableBotaoApagar = false,
  disableBotaoCancelarVenda = true,
  disableBotaoAdicionarItem = false,
  mostrarBotaoSalvarEFechar = false,
  mostrarBotaoNovoCarregando = false,
  mostrarBotaoVoltarCarregando = false,
  mostrarBotaoCancelarVendaCarregando = false,
  mostrarBotaoApagarCarregando = false,
  mostrarBotaoSalvarCarregando = false,
  mostrarBotaoSalvarEFecharCarregando = false,
  mostrarBotaoImpressaoCarregando = false,
  mostrarBotaoAdicionarItemCarregando = false,
  mostrarBotaoDevolucao = false,
  mostrarBotaoDevolucaoCarregando = false,

  aoClicarEmDevolucao,
  aoClicarEmNovo,
  aoClicarEmVoltar,
  aoClicarEmApagar,
  aoClicarEmSalvar,
  aoClicarEmSalvarEFechar,
  aoClicarEmImpressao,
  aoClicarEmCarne,
  aoClicarEmCancelarVenda,
  aoClicarEmAdicionarItem,
  conteudoFinal,
}) => {
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const shortScreen = useMediaQuery('(max-height:700px)');
  const theme = useTheme();
  const tamanhoBotao = shortScreen ? 'small' : 'medium';

  return (
    <Box
      gap={shortScreen ? 0.5 : 1}
      marginX={2}
      padding={shortScreen ? 0.5 : 1}
      paddingX={2}
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      minHeight={theme.spacing(shortScreen ? 5 : 6)}
      component={Paper}
      sx={smDown ? { flexDirection: 'column', alignItems: 'stretch', marginX: 1, paddingX: 1 } : undefined}
    >
      {/* Celular: os botões numa linha só, rolando de lado (em vez de quebrar
          em várias linhas e empurrar a tela pra baixo); o conteúdo final (ex.:
          modo de busca) vai numa linha própria embaixo. No computador esse Box
          é "display: contents" — não muda nada do layout de sempre. */}
      <Box
        sx={smDown ? {
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          overflowX: 'auto',
          flexWrap: 'nowrap',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          '& > *': { flexShrink: 0 },
          '& .MuiButton-root': { whiteSpace: 'nowrap' },
        } : { display: 'contents' }}
      >
      {(mostrarBotaoVoltar && !mostrarBotaoVoltarCarregando) && (
        <Button
          color='primary'
          disableElevation
          variant='outlined'
          size={tamanhoBotao}
          onClick={aoClicarEmVoltar}
          startIcon={<Icon>arrow_back</Icon>}
        >
          <RotuloBotao label="Voltar" hotkey="ESC" />
        </Button>
      )}

      {mostrarBotaoVoltarCarregando && (
        <Skeleton width={110} height={shortScreen ? 40 : 60} />
      )}
      {
        (
          mostrarBotaoVoltar &&
          (mostrarBotaoNovo || mostrarBotaoApagar || mostrarBotaoSalvar || mostrarBotaoSalvarEFechar)
        ) && (
          <Divider variant='middle' orientation='vertical' />
        )
      }

      {(mostrarBotaoSalvar && !mostrarBotaoSalvarCarregando) && (
        <Button
          color='primary'
          disableElevation
          disabled={disableBotaoSalvar}
          variant='contained'
          size={tamanhoBotao}
          onClick={aoClicarEmSalvar}
          startIcon={<Icon>save</Icon>}
        >
          <RotuloBotao label="Salvar" hotkey="F4" />
        </Button>
      )}

      {mostrarBotaoSalvarCarregando && (
        <Skeleton width={110} height={shortScreen ? 40 : 60} />
      )}

      {(mostrarBotaoSalvarEFechar && !mostrarBotaoSalvarEFecharCarregando && !smDown && !mdDown) && (
        <Button
          color='primary'
          disableElevation
          variant='outlined'
          size={tamanhoBotao}
          disabled={disableBotaoSalvarEFechar}
          onClick={aoClicarEmSalvarEFechar}
          startIcon={<Icon>save</Icon>}
        >
          <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
            Salvar e Fechar
          </Typography>
        </Button>
      )}

      {(mostrarBotaoSalvarEFecharCarregando && !smDown && !mdDown) && (
        <Skeleton width={180} height={shortScreen ? 40 : 60} />
      )}

      {(mostrarBotaoApagar && !mostrarBotaoApagarCarregando) && (
        <Button
          color='error'
          disableElevation
          variant='contained'
          size={tamanhoBotao}
          onClick={aoClicarEmApagar}
          disabled={disableBotaoApagar}
          startIcon={<Icon>delete</Icon>}
        >
          <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
            Apagar
          </Typography>
        </Button>
      )}

      {mostrarBotaoApagarCarregando && (
        <Skeleton width={110} height={shortScreen ? 40 : 60} />
      )}

      {(mostrarBotaoNovo && !mostrarBotaoNovoCarregando && !smDown) && (
        <Button
          color='primary'
          disableElevation
          variant='outlined'
          size={tamanhoBotao}
          onClick={aoClicarEmNovo}
          startIcon={<Icon>add</Icon>}
        >
          <RotuloBotao label={textoBotaoNovo} hotkey="F2" />
        </Button>
      )}

      {(mostrarBotaoNovoCarregando && !smDown) && (
        <Skeleton width={110} height={shortScreen ? 40 : 60} />
      )}

      {(mostrarBotaoAdicionarItem && !mostrarBotaoAdicionarItemCarregando) && (
        <Button
          color='primary'
          disableElevation
          variant='outlined'
          size={tamanhoBotao}
          disabled={disableBotaoAdicionarItem}
          onClick={aoClicarEmAdicionarItem}
          startIcon={<Icon>add_shopping_cart</Icon>}
        >
          <RotuloBotao label="Adicionar Item" hotkey="F3" />
        </Button>
      )}

      {mostrarBotaoAdicionarItemCarregando && (
        <Skeleton width={170} height={shortScreen ? 40 : 60} />
      )}

      {(mostrarBotaoImpressao && !mostrarBotaoImpressaoCarregando) && (
        <Button
          color='primary'
          disableElevation
          variant='outlined'
          size={tamanhoBotao}
          onClick={aoClicarEmImpressao}
          startIcon={<Icon>printoutlined</Icon>}
        >
          <RotuloBotao label="Imprimir" hotkey="F10" />
        </Button>
      )}
      {(mostrarBotaoCarne && !mostrarBotaoCarneCarregando) && (
        <Button
          color='primary'
          disableElevation
          variant='outlined'
          size={tamanhoBotao}
          onClick={aoClicarEmCarne}
          startIcon={<Icon>menu_book</Icon>}
        >
          <RotuloBotao label="Imprimir Carnê" />
        </Button>
      )}
      {mostrarBotaoCarneCarregando && (
        <Skeleton width={150} height={shortScreen ? 40 : 60} />
      )}
      {(mostrarBotaoDevolucao && !mostrarBotaoDevolucaoCarregando) && (
        <Button
          color='warning'
          disableElevation
          variant='contained'
          size={tamanhoBotao}
          onClick={aoClicarEmDevolucao}
          disabled={disableBotaoDevolucao}
          startIcon={<Icon>assignment_return</Icon>}
        >
          <Typography
            variant='button'
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            overflow="hidden"
          >
            Devolução
          </Typography>
        </Button>
      )}
      {(mostrarBotaoCancelarVenda && !mostrarBotaoCancelarVendaCarregando) && (
        <Button
          color="error"
          variant="outlined"
          disableElevation
          size={tamanhoBotao}
          disabled={disableBotaoCancelarVenda}
          onClick={aoClicarEmCancelarVenda}
          startIcon={<Icon>cancel</Icon>}
        >
          <Typography variant="button">
            Cancelar Venda
          </Typography>
        </Button>
      )}

      {mostrarBotaoCancelarVendaCarregando && (
        <Skeleton width={150} height={shortScreen ? 40 : 60} />
      )}

      {mostrarBotaoDevolucaoCarregando && (
        <Skeleton width={130} height={shortScreen ? 40 : 60} />
      )}

      </Box>

      {conteudoFinal && (
        <Box sx={{ ml: 'auto', width: { xs: '100%', sm: 'auto' } }}>
          {conteudoFinal}
        </Box>
      )}

    </Box >
  );
};